import { api } from './client';
import type {
  AdminProductListItemDto,
  AdminProductQueryDto,
  BrandDto,
  BrandUpsertDto,
  CategoryDto,
  CategoryReorderDto,
  CategoryUpsertDto,
  CollectionDto,
  CollectionUpsertDto,
  PagedRequest,
  PagedResult,
  ProductDetailDto,
  ProductReviewDto,
  ProductStatus,
  ProductUpsertDto,
  ReviewModerationDto,
  ReviewQueryDto,
  ReviewReplyDto,
  ProductAttributeDto,
  ProductAttributeUpsertDto,
  CategoryAttributeDto,
  CategoryAttributeAssignmentDto,
} from './types';

/* ------------------------------------------------------------------ *
 *  Yonetim katalogu — 28 uc
 *  urunler (8), kategoriler (7), markalar (4), koleksiyonlar (5),
 *  yorumlar (4)
 * ------------------------------------------------------------------ */

export const adminProductsApi = {
  /** 1. Yonetim urun listesi: taslak ve silinmisleri de kapsar. */
  search: (query: AdminProductQueryDto = {}) =>
    api.get<PagedResult<AdminProductListItemDto>>('/api/admin/products', query),

  /**
   * 2. Duzenleme formunu dolduran tam urun verisi.
   *
   * Uc, gorunum DTO'sunu degil dogrudan kaydetme sozlesmesini (upsert)
   * dondurur; boylece form alanlari birebir eslesir.
   */
  getForEdit: (id: number) => api.get<ProductUpsertDto>(`/api/admin/products/${id}`),

  /** 3. Urun olusturur; varyantlar seceneklerden uretilir. */
  create: (dto: ProductUpsertDto) => api.post<ProductDetailDto>('/api/admin/products', dto),

  /** 4. Urunu gunceller. */
  update: (id: number, dto: ProductUpsertDto) =>
    api.put<ProductDetailDto>(`/api/admin/products/${id}`, dto),

  /** 5. Yumusak siler; kayit korunur, vitrinden kalkar. */
  remove: (id: number) => api.deleteCommand(`/api/admin/products/${id}/delete`),

  /** 6. Silinmis urunu geri alir. */
  restore: (id: number) => api.postCommand(`/api/admin/products/${id}/restore`),

  /** 7. Secili urunlerin durumunu toplu degistirir. */
  setStatus: (productIds: number[], status: ProductStatus) =>
    api.postCommand('/api/admin/products/bulk-status', { productIds, status }),

  /** 8. Fiyat araligi, stok ve puan gibi turetilmis alanlari yeniden hesaplar. */
  recalculate: (id: number) => api.postCommand(`/api/admin/products/${id}/recalculate`),
};

export const adminCategoriesApi = {
  /** 9. Kategori agaci; pasifleri de dondurur. */
  tree: (includeInactive = true) =>
    api.get<CategoryDto[]>('/api/admin/categories', { includeInactive }),

  /** 10. Kategori detayi. */
  getById: (id: number) => api.get<CategoryDto>(`/api/admin/categories/${id}`),

  /** 11. Kategori olusturur. */
  create: (dto: CategoryUpsertDto) => api.post<CategoryDto>('/api/admin/categories', dto),

  /** 12. Kategori gunceller. */
  update: (id: number, dto: CategoryUpsertDto) =>
    api.put<CategoryDto>(`/api/admin/categories/${id}`, dto),

  /** 13. Kategoriyi siler. Alt kategorisi veya urunu varsa reddedilir. */
  remove: (id: number) => api.deleteCommand(`/api/admin/categories/${id}/delete`),

  /** 14. Siralama ve ust kategori degisikligini toplu uygular. */
  reorder: (dto: CategoryReorderDto) => api.postCommand('/api/admin/categories/reorder', dto),

  /** 15. Kategorilerdeki urun sayaclarini yeniden hesaplar. */
  recalculateCounts: () => api.postCommand('/api/admin/categories/recalculate-counts'),
};

export const adminBrandsApi = {
  /** 16. Marka listesi. */
  search: (query: PagedRequest = {}) =>
    api.get<PagedResult<BrandDto>>('/api/admin/brands', query),

  /** 17. Marka olusturur. */
  create: (dto: BrandUpsertDto) => api.post<BrandDto>('/api/admin/brands', dto),

  /** 18. Marka gunceller. */
  update: (id: number, dto: BrandUpsertDto) => api.put<BrandDto>(`/api/admin/brands/${id}`, dto),

  /** 19. Marka siler. */
  remove: (id: number) => api.deleteCommand(`/api/admin/brands/${id}/delete`),
};

export const adminCollectionsApi = {
  /** 20. Koleksiyon listesi. */
  search: (query: PagedRequest = {}) =>
    api.get<PagedResult<CollectionDto>>('/api/admin/collections', query),

  /** 21. Koleksiyon olusturur (elle secim veya kurallı). */
  create: (dto: CollectionUpsertDto) => api.post<CollectionDto>('/api/admin/collections', dto),

  /** 22. Koleksiyon gunceller. */
  update: (id: number, dto: CollectionUpsertDto) =>
    api.put<CollectionDto>(`/api/admin/collections/${id}`, dto),

  /** 23. Koleksiyon siler. */
  remove: (id: number) => api.deleteCommand(`/api/admin/collections/${id}/delete`),

  /** 24. Kurallı koleksiyonun uyelerini yeniden hesaplar. */
  refresh: (id: number) => api.postCommand(`/api/admin/collections/${id}/refresh`),
};

export const adminReviewsApi = {
  /** 25. Yorum listesi; onay bekleyenler filtrelenebilir. */
  search: (query: ReviewQueryDto = {}) =>
    api.get<PagedResult<ProductReviewDto>>('/api/admin/reviews', query),

  /** 26. Yorumu onaylar veya reddeder. */
  moderate: (id: number, dto: ReviewModerationDto) =>
    api.postCommand(`/api/admin/reviews/${id}/moderate`, dto),

  /** 27. Yoruma magaza yaniti yazar. */
  reply: (id: number, dto: ReviewReplyDto) =>
    api.postCommand(`/api/admin/reviews/${id}/reply`, dto),

  /** 28. Yorumu siler. */
  remove: (id: number) => api.deleteCommand(`/api/admin/reviews/${id}/delete`),
};

export const adminAttributesApi = {
  /** 29. Özellik listesi. */
  getAll: (activeOnly = false) =>
    api.get<ProductAttributeDto[]>('/api/admin/attributes', { activeOnly }),

  /** 30. Özellik detayı. */
  getById: (id: number) =>
    api.get<ProductAttributeDto>(`/api/admin/attributes/${id}`),

  /** 31. Özellik oluşturur. */
  create: (dto: ProductAttributeUpsertDto) =>
    api.post<ProductAttributeDto>('/api/admin/attributes', dto),

  /** 32. Özellik günceller. */
  update: (id: number, dto: ProductAttributeUpsertDto) =>
    api.put<ProductAttributeDto>(`/api/admin/attributes/${id}`, dto),

  /** 33. Özellik siler. */
  remove: (id: number) =>
    api.deleteCommand(`/api/admin/attributes/${id}/delete`),

  /** 34. Kategoriye ait özellikleri getirir. */
  getForCategory: (categoryId: number) =>
    api.get<CategoryAttributeDto[]>(`/api/admin/attributes/categories/${categoryId}`),

  /** 35. Kategoriye özellikleri bağlar. */
  assignToCategory: (categoryId: number, assignments: CategoryAttributeAssignmentDto[]) =>
    api.postCommand(`/api/admin/attributes/categories/${categoryId}`, assignments),
};
